import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Clock, FileQuestion, AlertCircle, ArrowLeft, Play, Repeat, Ban, Shield } from "lucide-react";
import api from "@/lib/api";

export default function ExamIntro() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/assessments/${id}`).then(r => setA(r.data));
    api.get(`/assessments/${id}/student-status`).then(r => setStatus(r.data));
  }, [id]);
  if (!a) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  const allow = a.allow_reattempts !== false;
  const maxAtt = a.max_attempts;
  const policyLabel = !allow
    ? "Single attempt only"
    : maxAtt
      ? `Up to ${maxAtt} attempt${maxAtt === 1 ? "" : "s"}`
      : "Unlimited attempts";
  const canAttempt = status ? status.can_attempt : true;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link to="/student/upcoming" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to upcoming
        </Link>
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-medium">Assessment</div>
          <h1 className="mt-2 text-3xl tracking-tight font-semibold text-slate-900">{a.title}</h1>

          <div className="mt-6 grid grid-cols-3 gap-3">
            <Stat icon={Clock} label="Duration" value={`${a.duration} min`} />
            <Stat icon={FileQuestion} label="Questions" value={a.questions?.length || 0} />
            <Stat icon={AlertCircle} label="Pass" value={`${a.passing_marks}%`} />
          </div>

          <div className="mt-4 flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3 bg-slate-50">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Repeat className="w-4 h-4 text-slate-500" />
              <span>{policyLabel}</span>
            </div>
            {status && (
              <span data-testid="exam-attempts-count" className="text-xs text-slate-600">
                {status.attempts} previous attempt{status.attempts === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {!canAttempt ? (
            <div data-testid="exam-blocked-banner" className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <div className="font-medium mb-1 flex items-center gap-2"><Ban className="w-4 h-4" /> You cannot start this assessment</div>
              <div className="text-red-900/90">{status.reason}</div>
            </div>
          ) : (
            <>
              <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <div className="font-medium mb-1">Before you begin</div>
                <ul className="list-disc pl-5 space-y-1 text-amber-900/90">
                  <li>The timer starts the moment you click <strong>Begin</strong>.</li>
                  <li>Your answers auto-save as you go. Submit before the timer reaches zero.</li>
                  <li>You can flag questions for review and navigate freely via the palette.</li>
                </ul>
              </div>
              {a.proctoring_enabled !== false && (
                <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
                  <div className="font-medium mb-1 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-600" /> Proctoring is active for this assessment
                  </div>
                  <ul className="list-disc pl-5 space-y-1 text-blue-900/90 mt-1">
                    <li>The exam opens in <strong>fullscreen</strong>. Exiting counts as a warning.</li>
                    <li>Switching tabs, losing window focus, right-clicking and copy/paste are tracked.</li>
                    <li>After <strong>{a.violation_threshold || 3} warnings</strong> the exam auto-submits with your current answers.</li>
                  </ul>
                </div>
              )}
            </>
          )}

          <button
            data-testid="exam-start-button"
            disabled={!canAttempt}
            onClick={() => navigate(`/student/exam/${id}/take`)}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg px-6 py-3 font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
            <Play className="w-4 h-4" /> Begin assessment
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <Icon className="w-4 h-4 text-blue-600" />
      <div className="mt-2 text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
